import { useAtomValue, useSetAtom } from "jotai";
import { Fragment, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  activeProfileAtom,
  activeStudioProfileAtom,
  displayPersonalProfileAtom,
} from "../../../../atoms/user/activeProfileAtom";
import {
  activeUserAtom,
  isActiveUserEngineerStudioManagerOrArtistAtom,
} from "../../../../atoms/user/activeUserAtom";
import { useMediaQueryBreakpoint } from "../../../../hooks/useMediaQuery";
import { isAuthenticatedRoute } from "../../../../routes";
import { SCREENS } from "../../../../routes/screens";
import { useAppSelector, useLogout } from "../../../../store/hooks";
import User from "../../../../store/models/user";
import { isUserVerifiedAdminSelector } from "../../../../store/selectors/userInfoSelectors";
import {
  getMyMusicRoute,
  getProfileScreenRoute,
  getStudioScreenRoute,
} from "../../../../store/utils/routeGetters";
import { emitAnalyticsTrackingEvent } from "../../../../utils/analyticsUtils";
import { accountSettingsModalOpenAtom } from "../../AccountSettingsModal/atoms";
import { DropdownNavMenu } from "../../DrowdownNavMenu/DropdownNavMenu";
import { NavAddStudioProfileButton } from "./components/NavAddStudioProfileButton/NavAddStudioProfileButton";
import { NavDropDownHelpButton } from "./components/NavDropDownHelpButton/NavDropDownHelpButton";
import {
  NAV_LIST_ITEM_VARIANT,
  NavDropDownListItem,
} from "./components/NavDropDownListItem/NavDropDownListItem";
import { NavEditServicesButton } from "./components/NavEditServicesButton/NavEditServicesButton";
import { ProfileRow } from "./components/ProfileRow/ProfileRow";
import "./NavProfileContent.css";
import { MyMusicTabs } from "../../../screens/MyMusic/constants";

enum NavProfileContentItem {
  ADMIN_DASHBOARD,
  DASHBOARD,
  BOOKINGS,
  MY_MUSIC,
  PROFILE,
  SETTINGS,
  SEARCH,
  COMMUNITY,
  FAVORITES,
  ARTIST_PRICING,
  ENGINEER_AND_STUDIO_PRICING,
}

export interface NavProfileContentProps {
  user: User | undefined;
  onClose: () => void;
}

const loggedInItems = [
  NavProfileContentItem.BOOKINGS,
  NavProfileContentItem.MY_MUSIC,
  NavProfileContentItem.PROFILE,
  NavProfileContentItem.SETTINGS,
  NavProfileContentItem.FAVORITES,
];

const engineerStudioManagerAndArtistItems = [
  NavProfileContentItem.DASHBOARD,
  ...loggedInItems,
];

const adminItems = [NavProfileContentItem.ADMIN_DASHBOARD, ...loggedInItems];

export const NavProfileContent = ({
  user,
  onClose,
}: NavProfileContentProps) => {
  const { isMobile } = useMediaQueryBreakpoint();
  const setIsAccountSettingsModalOpen = useSetAtom(
    accountSettingsModalOpenAtom,
  );
  const { userStudios } = useAppSelector((state) => state.accountInfo);
  const isEngineerStudioManagerOrArtist = useAtomValue(
    isActiveUserEngineerStudioManagerOrArtistAtom,
  );
  const isUserAdmin = useAppSelector(isUserVerifiedAdminSelector);
  const { pathname } = useLocation();
  const isOnAuthenticatedScreen = isAuthenticatedRoute(pathname);
  const redirectPath = isOnAuthenticatedScreen
    ? SCREENS.SIGNED_OUT_HOME_SCREEN
    : undefined;
  const logout = useLogout(redirectPath);
  const activeProfile = useAtomValue(activeProfileAtom);
  const activeStudioProfile = useAtomValue(activeStudioProfileAtom);
  const activeUser = useAtomValue(activeUserAtom);

  const username = activeProfile?.username;
  const profilePath = activeStudioProfile
    ? getStudioScreenRoute(username ?? "")
    : getProfileScreenRoute(username ?? "");

  const displayPersonalProfile = useAtomValue(displayPersonalProfileAtom);

  const myMusicPath = useMemo(() => {
    if (activeUser?.disciplines?.artist) {
      return getMyMusicRoute(MyMusicTabs.Releases);
    }
    return getMyMusicRoute(MyMusicTabs.Library);
  }, [activeUser]);

  const navProfileContentOptions = useMemo(
    () => ({
      [NavProfileContentItem.ADMIN_DASHBOARD]: {
        title: "Dashboard",
        path: SCREENS.ADMIN_DASHBOARD,
        trackingEvent: "clicked_admin_dashboard",
      },
      [NavProfileContentItem.DASHBOARD]: {
        title: "Dashboard",
        path: SCREENS.DASHBOARD,
        trackingEvent: "clicked_dashboard",
      },
      [NavProfileContentItem.BOOKINGS]: {
        title: "Bookings",
        path: SCREENS.PROJECTS_NO_TAB,
        trackingEvent: "clicked_bookings",
      },
      [NavProfileContentItem.MY_MUSIC]: {
        title: "My Music",
        path: myMusicPath,
        trackingEvent: "clicked_my_music",
      },
      [NavProfileContentItem.PROFILE]: {
        title: "My Profile",
        path: profilePath,
        trackingEvent: "clicked_profile",
      },
      [NavProfileContentItem.SETTINGS]: {
        title: "Settings",
        path: SCREENS.SETTINGS_SCREEN,
        trackingEvent: "clicked_settings",
      },
      [NavProfileContentItem.SEARCH]: {
        title: "Explore Profiles",
        path: SCREENS.SEARCH,
        trackingEvent: "clicked_search",
      },
      [NavProfileContentItem.ARTIST_PRICING]: {
        title: "Artist Pricing",
        path: SCREENS.ADD_ARTIST_SUBSCRIPTION,
        trackingEvent: "clicked_pricing",
      },
      [NavProfileContentItem.ENGINEER_AND_STUDIO_PRICING]: {
        title: "Engineers Pricing",
        path: SCREENS.ADD_SUBSCRIPTION,
        trackingEvent: "clicked_pricing",
      },
      [NavProfileContentItem.COMMUNITY]: {
        title: "Community",
        path: SCREENS.COMMUNITY,
        trackingEvent: "clicked_community",
      },
      [NavProfileContentItem.FAVORITES]: {
        title: "Favorites",
        path: SCREENS.DASHBOARD_FAVORITES,
        trackingEvent: "clicked_favorites",
      },
    }),
    [myMusicPath, profilePath],
  );

  const defaultItems = [
    NavProfileContentItem.SEARCH,
    NavProfileContentItem.COMMUNITY,
    NavProfileContentItem.ARTIST_PRICING,
    NavProfileContentItem.ENGINEER_AND_STUDIO_PRICING,
  ];

  const profileListItems = useMemo(() => {
    if (isUserAdmin) {
      return adminItems;
    }

    if (isEngineerStudioManagerOrArtist) {
      return engineerStudioManagerAndArtistItems;
    }

    return loggedInItems;
  }, [isUserAdmin, isEngineerStudioManagerOrArtist]);

  return (
    <ul className="nav-profile-content">
      {user && (
        <>
          {displayPersonalProfile && (
            <li className="profile-row-li">
              <ProfileRow onClick={onClose} />
            </li>
          )}
          {Array.isArray(userStudios) &&
            userStudios.map((studio) => {
              return (
                <Fragment key={`studio-${studio.id}`}>
                  <li className="profile-row-li" key={`studio-${studio.id}`}>
                    <ProfileRow studio={studio} onClick={onClose} />
                  </li>
                  <hr />
                </Fragment>
              );
            })}
          {profileListItems.map((listItem) => {
            const { title, path, trackingEvent } =
              navProfileContentOptions[listItem];
            return listItem === NavProfileContentItem.SETTINGS ? (
              <NavDropDownListItem
                text={title}
                key={`profile-list-item-${title}`}
                variant={NAV_LIST_ITEM_VARIANT.BUTTON}
                onClick={() => {
                  emitAnalyticsTrackingEvent(trackingEvent, {});
                  onClose();
                  setIsAccountSettingsModalOpen(true);
                }}
              />
            ) : (
              <NavDropDownListItem
                key={`profile-list-item-${title}`}
                text={title}
                variant={NAV_LIST_ITEM_VARIANT.LINK}
                path={path}
                currentPath={pathname}
                onClick={() => {
                  emitAnalyticsTrackingEvent(trackingEvent, {});
                  onClose();
                }}
              />
            );
          })}
          <hr />
        </>
      )}
      {user && (
        <>
          {user.engineer && !user.engineer.deleted && (
            <NavEditServicesButton engineer={user.engineer} user={user} />
          )}
          <NavAddStudioProfileButton onClose={onClose} />
          <hr />
        </>
      )}
      {isMobile && !user && <DropdownNavMenu isMobile />}
      {defaultItems.map((listItem) => {
        const { title, path, trackingEvent } =
          navProfileContentOptions[listItem];
        return (
          <NavDropDownListItem
            key={`default-list-item-${listItem}`}
            text={title}
            variant={NAV_LIST_ITEM_VARIANT.LINK}
            path={path}
            currentPath={pathname}
            onClick={() => {
              emitAnalyticsTrackingEvent(trackingEvent, {});
              onClose();
            }}
          />
        );
      })}
      <NavDropDownHelpButton onClose={onClose} />
      {user && (
        <>
          <hr />
          <NavDropDownListItem
            text="Log Out"
            variant={NAV_LIST_ITEM_VARIANT.BUTTON}
            onClick={logout}
          />
        </>
      )}
    </ul>
  );
};
